import React, { useContext, useEffect, useState } from 'react';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core';
import { empty, req_stock_barcode_list } from '../../Helper/Constants';
import { de } from '../../Helper/Helper';
import API_Service, { axiosDefaultConfig, axiosPutConfig, axiosURIencode } from '../../Services/API_Service';
import AppContext from '../../Services/AppContext';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles((theme) => ({
	dialogTitle: { paddingBottom: theme.spacing(0.5) },
	dialogContent: { paddingTop: theme.spacing(1) },
	textField: { marginBottom: theme.spacing(2.5) },
	formControl: { marginBottom: theme.spacing(2.5) },
	dialogActions: { padding: theme.spacing(1, 3, 2) },
	saveButton: { minWidth: 100, fontWeight: theme.typography.fontWeightMedium }
}));

export default function AddEditBarcode({ open, onClose, language, data, product_code, uomList, chainList, onUpdate }) {
	const classes = useStyles();
	const [gState] = useContext(AppContext);
	const { enqueueSnackbar } = useSnackbar();
	const [barcode, setBarcode] = useState(empty);
	const [selectedChain, setSelectedChain] = useState(empty);
	const [selectedUom, setSelectedUom] = useState(empty);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (open) {
			if (data !== empty && data !== null && data !== undefined) {
				setBarcode(data.barcode || empty);
				setSelectedChain(data.chain_id || empty);
				setSelectedUom(data.uom || empty);
			} else {
				setBarcode(empty);
				setSelectedChain(empty);
				setSelectedUom(empty);
			}
		}
	}, [open, data]);

	const snack = (msg, type = 'success') => {
		enqueueSnackbar(msg, {
			variant: type,
			preventDuplicate: true,
			autoHideDuration: type === 'success' ? 2000 : 3000
		});
	};

	const closeDialog = () => {
		setBarcode(empty);
		setSelectedChain(empty);
		setSelectedUom(empty);
		onClose();
	};

	const save = () => {
		if (barcode === empty || !barcode) {
			snack(language.barcode + ' ' + language.field_required, 'warning');
			return;
		}
		if (selectedChain === empty || !selectedChain) {
			snack(language.select_chain + ' ' + language.field_required, 'warning');
			return;
		}
		if (selectedUom === empty || !selectedUom) {
			snack(language.select_uom + ' ' + language.field_required, 'warning');
			return;
		}
		setSaving(true);

		if (data !== empty && data !== null && data !== undefined && data.id) {
			const params = axiosURIencode({
				id: data.id,
				barcode: barcode,
				uom: selectedUom,
				chain_id: selectedChain
			});
			API_Service.put(req_stock_barcode_list, params, axiosPutConfig(gState.client))
				.then(({ data: resData }) => {
					setSaving(false);
					const { result, message } = resData;
					if (parseInt(result) === 1) {
						snack(message);
						closeDialog();
						onUpdate();
					} else {
						snack(message, 'error');
					}
				})
				.catch(err => { setSaving(false); de(err.message); snack(language.update_fail, 'error'); });
		} else {
			const formdata = new FormData();
			formdata.append('product_code', product_code);
			formdata.append('barcode', barcode);
			formdata.append('uom', selectedUom);
			formdata.append('chain_id', selectedChain);
			API_Service.post(req_stock_barcode_list, formdata, axiosDefaultConfig(gState.client))
				.then(({ data: resData }) => {
					setSaving(false);
					const { result, message } = resData;
					if (parseInt(result) === 1) {
						snack(message);
						closeDialog();
						onUpdate();
					} else {
						snack(message, 'error');
					}
				})
				.catch(err => { setSaving(false); de(err.message); snack(language.update_fail, 'error'); });
		}
	};

	const isEdit = data !== empty && data !== null && data !== undefined && data.id;

	return (
		<Dialog onClose={closeDialog} open={open} maxWidth="sm" fullWidth>
			<DialogTitle className={classes.dialogTitle}>
				{isEdit ? language.edit_barcode : language.new_barcode}
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				<TextField
					className={classes.textField}
					fullWidth
					variant="outlined"
					id="input-barcode"
					label={language.barcode}
					value={barcode === empty ? '' : barcode}
					placeholder={language.barcode}
					onChange={(e) => setBarcode(e.target.value)}
				/>
				<FormControl className={classes.formControl} fullWidth variant="outlined">
					<InputLabel>{language.select_chain}</InputLabel>
					<Select
						value={selectedChain === empty ? '' : selectedChain}
						onChange={(e) => setSelectedChain(e.target.value)}
						label={language.select_chain}
					>
						{chainList && chainList.map((chain) => (
							<MenuItem key={chain.cust_chain_id} value={chain.cust_chain_id}>
								{chain.cust_chain_name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl className={classes.formControl} fullWidth variant="outlined">
					<InputLabel>{language.select_uom}</InputLabel>
					<Select
						value={selectedUom === empty ? '' : selectedUom}
						onChange={(e) => setSelectedUom(e.target.value)}
						label={language.select_uom}
					>
						{uomList && uomList.map((uom) => (
							<MenuItem key={uom.product_uom} value={uom.product_uom}>
								{uom.product_uom}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</DialogContent>
			<DialogActions className={classes.dialogActions}>
				<Button onClick={closeDialog} disabled={saving}>
					{language.cancel}
				</Button>
				<Button
					onClick={save}
					variant="contained"
					color="primary"
					disabled={saving}
					className={classes.saveButton}
				>
					{saving ? <CircularProgress size={20} /> : language.save}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
